<template>
  <div class="animated fadeIn">
    <div
      class="dashboard-header d-flex justify-content-between align-items-center mb-3"
    >
      <h4 v-if="isMasterUser && company" class="mb-0">
        Dashboard da {{ company.fantasyName }}
      </h4>
      <span v-else></span>
      <display-mode-toggle
        v-if="canViewCharts || showMetrics"
        v-model="displayMode"
        storage-key="dashboard:displayMode:complaint"
      />
    </div>

    <!-- Estatísticas de Denúncias para Usuários de Empresa com Whitelabel (no topo) -->
    <b-card v-if="showMetrics" class="metrics-container mb-4">
      <div class="metrics-grid">
        <div class="metric-section">
          <div class="metric-header">
            <div class="metric-icon">
              <i class="fa fa-list"></i>
            </div>
            <p class="metric-label">TOTAL DENÚNCIAS</p>
          </div>
          <h3 class="metric-value">{{ totalComplaints }}</h3>
          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: totalProgress + '%' }"
            ></div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="metric-section">
          <div class="metric-header">
            <div class="metric-icon">
              <i class="fa fa-plus"></i>
            </div>
            <p class="metric-label">NOVAS</p>
          </div>
          <h3 class="metric-value">{{ formatMetric(newComplaints) }}</h3>
          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: newProgress + '%' }"
            ></div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="metric-section">
          <div class="metric-header">
            <div class="metric-icon">
              <i class="fa fa-cog"></i>
            </div>
            <p class="metric-label">EM PROCESSAMENTO</p>
          </div>
          <h3 class="metric-value">
            {{ formatMetric(processingComplaints) }}
          </h3>
          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: processingProgress + '%' }"
            ></div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="metric-section">
          <div class="metric-header">
            <div class="metric-icon">
              <i class="fa fa-check"></i>
            </div>
            <p class="metric-label">CONCLUÍDAS</p>
          </div>
          <h3 class="metric-value">{{ formatMetric(concludedComplaints) }}</h3>
          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: completedProgress + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </b-card>

    <list-pendencies
      v-if="profile.role !== 'root' && profile.role !== 'company-root'"
    ></list-pendencies>

    <list-notifications
      :company="effectiveCompanyId"
      type="complaint"
      :show-inactive-for-user="isCompanyUserWithWhitelabel"
    ></list-notifications>

    <b-row v-if="canViewCharts" class="mb-4">
      <b-col sm="6" lg="4" class="mb-3">
        <b-card no-body class="chart-card chart-animation h-100">
          <b-card-body>
            <status-chart
              :company="effectiveCompanyId"
              type="complaint"
              title="Qtd. Solicitações x Situação"
              :style="{ 'max-height': maxHeight }"
            ></status-chart>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col sm="6" lg="8" class="mb-3">
        <b-card no-body class="chart-card chart-animation h-100">
          <b-card-body>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0">Denúncias Recebidas vs Concluídas por Mês</h6>
              <div class="year-selector d-flex align-items-center">
                <button
                  class="year-nav-btn"
                  @click="previousYear"
                  :disabled="selectedYear <= minYear"
                  title="Ano anterior"
                >
                  <i class="fa fa-chevron-left"></i>
                </button>
                <span class="year-display">{{ selectedYear }}</span>
                <button
                  class="year-nav-btn"
                  @click="nextYear"
                  :disabled="selectedYear >= currentYear"
                  title="Próximo ano"
                >
                  <i class="fa fa-chevron-right"></i>
                </button>
              </div>
            </div>
            <complaints-per-month
              :company="effectiveCompanyId"
              :year="selectedYear"
            ></complaints-per-month>
          </b-card-body>
        </b-card>
      </b-col>
      <template v-if="company.classification">
        <b-col
          v-if="company.classification.level.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Denúncias x Classificação/Classe Interna"
                type="complaint"
                classification="level"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.nature.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Denúncias x Natureza"
                type="complaint"
                classification="nature"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.origin.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Denúncias x Origem"
                type="complaint"
                classification="origin"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.receivingMeans.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Denúncias x Meio Recebimento"
                type="complaint"
                classification="receivingMeans"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.channelConfidence.enabled"
          sm="6"
          lg="4"
        >
          <b-card no-body>
            <b-card-body>
              <classification-chart
                :company="company._id"
                title="Qtd. Denúncias x Confiança no Canal"
                type="complaint"
                classification="channelConfidence"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.reportingAgent.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Denúncias x Agente Denunciante"
                type="complaint"
                classification="reportingAgent"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.involvedDepartment.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Denúncias x Setor da Ocorrência"
                type="complaint"
                classification="involvedDepartment"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.responsibleDepartment.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Denúncias x Setor Responsável"
                type="complaint"
                classification="responsibleDepartment"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.businessUnit.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Denúncias x Unidade"
                type="complaint"
                classification="businessUnit"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.businessGroup.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Denúncias x Empresa do Grupo"
                type="complaint"
                classification="businessGroup"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.relatedProject.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Solicitações x Contrato/Projeto"
                type="complaint"
                classification="relatedProject"
                :style="{ 'max-height': maxHeight }"
              >
              </classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col
          v-if="company.classification.complaintClosure.enabled"
          sm="6"
          lg="4"
          class="mb-3"
        >
          <b-card no-body class="chart-card chart-animation h-100">
            <b-card-body>
              <classification-chart
                :company="effectiveCompanyId"
                title="Qtd. Denúncias x Indicador de Encerramento"
                type="complaint"
                classification="complaintClosure"
                :style="{ 'max-height': maxHeight }"
              ></classification-chart>
            </b-card-body>
          </b-card>
        </b-col>
      </template>
      <b-col sm="6" lg="4" class="mb-3">
        <b-card no-body class="chart-card chart-animation h-100">
          <b-card-body>
            <complaints-time-box-plot
              :company="effectiveCompanyId"
            ></complaints-time-box-plot>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import ComplaintsTimeBoxPlot from "../charts/complaints/ComplaintsTimeBoxPlot";
import ComplaintsPerMonth from "../charts/complaints/ComplaintsPerMonth";
import ListNotifications from "../list-notifications";
import ListPendencies from "../list-pendencies";
import ClassificationChart from "../charts/ClassificationChart";
import StatusChart from "../charts/StatusChart";
import UntriagedComplaints from "../charts/complaints/UntriagedComplaints";
import DisplayModeToggle from "../DisplayModeToggle.vue";
import { companyStatusChart } from "../../../api/charts";

const STORAGE_KEY = "dashboard:displayMode:complaint";

export default {
  name: "ComplaintsDashboard",
  components: {
    ListNotifications,
    ListPendencies,
    ComplaintsTimeBoxPlot,
    ComplaintsPerMonth,
    StatusChart,
    ClassificationChart,
    UntriagedComplaints,
    DisplayModeToggle,
  },
  props: {
    company: {
      type: Object,
      default: null,
    },
    filterCompanyId: {
      type: [Number, String],
      default: undefined, // undefined = não foi passado, null = "Todas as Empresas"
    },
  },
  provide() {
    return {
      dashboardDisplayMode: this.dashboardDisplayMode,
    };
  },
  data() {
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 4; // Últimos 5 anos
    const stored = localStorage.getItem(STORAGE_KEY);
    const initialMode = stored === "percent" ? "percent" : "absolute";
    return {
      maxHeight: "100%",
      statusData: [],
      selectedYear: currentYear,
      currentYear: currentYear,
      minYear: minYear,
      displayMode: initialMode,
      dashboardDisplayMode: Vue.observable({ mode: initialMode }),
    };
  },
  watch: {
    displayMode(newMode) {
      this.dashboardDisplayMode.mode = newMode;
    },
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    isMasterUser() {
      return (
        this.profile.role === "root" || this.profile.role === "company-root"
      );
    },
    isCompanyManager() {
      return (
        this.profile.role === "company-root" &&
        this.profile.managedCompanies &&
        this.profile.managedCompanies.length > 0
      );
    },
    // Verifica se deve exibir as métricas de denúncias
    showMetrics() {
      // Root e Company-root sempre podem ver
      if (
        this.profile.role === "root" ||
        this.profile.role === "company-root"
      ) {
        return true;
      }
      // Usuários com cargo customizado (numérico) vêem se for empresa com whitelabel
      const isCompanyUser = typeof this.profile.role === "number";
      const hasWhitelabel =
        this.company && this.company.whitelabel && this.company.whitelabel.fqdn;
      return isCompanyUser && hasWhitelabel;
    },
    // Verifica se é um usuário de empresa (company-user) em empresa com whitelabel
    isCompanyUserWithWhitelabel() {
      // Verifica se o papel do usuário é numérico (custom role/company-user)
      const isCompanyUser = typeof this.profile.role === "number";
      // Verifica se a empresa tem whitelabel configurado
      const hasWhitelabel =
        this.company && this.company.whitelabel && this.company.whitelabel.fqdn;
      return isCompanyUser && hasWhitelabel;
    },
    // Estatísticas de denúncias
    totalComplaints() {
      return this.statusData.reduce((sum, item) => sum + item.count, 0);
    },
    newComplaints() {
      return this.statusData.find((item) => item._id === 0)?.count || 0;
    },
    processingComplaints() {
      return this.statusData.find((item) => item._id === 1)?.count || 0;
    },
    concludedComplaints() {
      return this.statusData.find((item) => item._id === 2)?.count || 0;
    },
    totalProgress() {
      const total = this.totalComplaints;
      return total > 0 ? Math.min((total / 50) * 100, 100) : 0;
    },
    newProgress() {
      const total = this.totalComplaints;
      const newCount = this.newComplaints;
      return total > 0 ? Math.min((newCount / total) * 100, 100) : 0;
    },
    processingProgress() {
      const total = this.totalComplaints;
      const processingCount = this.processingComplaints;
      return total > 0 ? Math.min((processingCount / total) * 100, 100) : 0;
    },
    completedProgress() {
      const total = this.totalComplaints;
      const completedCount = this.concludedComplaints;
      return total > 0 ? Math.min((completedCount / total) * 100, 100) : 0;
    },
    // ID da empresa para filtrar os gráficos
    effectiveCompanyId() {
      // Se filterCompanyId foi passado explicitamente (incluindo null para "Todas")
      if (this.filterCompanyId !== undefined) {
        return this.filterCompanyId; // Pode ser null (Todas) ou um ID específico
      }
      // Se não foi passado, usar o company._id normal
      return this.company?._id;
    },
    canViewCharts() {
      if (parseInt(this.profile.role) === this.profile.role) {
        return this.permissions.complaint && this.permissions.complaint.read;
      }
      return true;
    },
  },
  async mounted() {
    if (this.showMetrics) {
      await this.loadStatusData();
    }
  },
  methods: {
    formatMetric(count) {
      if (this.displayMode !== "percent") return count;
      const total = this.totalComplaints;
      if (!total || total <= 0) return "0,00%";
      const pct = (count / total) * 100;
      return `${pct.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    },
    async loadStatusData() {
      try {
        const response = await companyStatusChart(
          this.effectiveCompanyId,
          "complaint"
        );
        this.statusData = response.data;
      } catch (error) {
        console.error("Erro ao carregar estatísticas:", error);
        this.statusData = [];
      }
    },
    previousYear() {
      if (this.selectedYear > this.minYear) {
        this.selectedYear--;
      }
    },
    nextYear() {
      if (this.selectedYear < this.currentYear) {
        this.selectedYear++;
      }
    },
  },
};
</script>

<style scoped>
.chart-card {
  height: 100%;
  min-height: 350px;
}

/* Estilos para o card de métricas */
.metrics-container {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
  margin-bottom: 2rem;
}

.metrics-grid {
  display: flex;
  align-items: stretch;
  min-height: 120px;
}

.metric-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 1.5rem 1rem;
  position: relative;
}

.metric-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.metric-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #000000;
}

.metric-label {
  font-size: 0.85rem;
  color: #000000;
  margin: 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  font-size: 2rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  color: #000000;
  line-height: 1;
}

.progress-container {
  width: 75%;
  height: 5px;
  background-color: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #4299e1;
  border-radius: 2px;
  transition: width 0.4s ease;
}

.divider {
  width: 1px;
  align-self: stretch;
  background: repeating-linear-gradient(
    to bottom,
    #cbd5e0 0px,
    #cbd5e0 6px,
    transparent 6px,
    transparent 12px
  );
  margin: 0;
}

.dashboard-card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
}

@media (max-width: 768px) {
  .metrics-grid {
    flex-direction: column;
    min-height: auto;
  }

  .divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .metric-section {
    padding: 1rem;
  }

  .metric-icon {
    width: 22px;
    height: 22px;
    font-size: 14px;
  }

  .metric-value {
    font-size: 1.75rem;
  }

  .metric-label {
    font-size: 0.8rem;
  }
}

/* Estilos para seletor de ano com setas */
.year-selector {
  gap: 0.75rem;
}

.year-nav-btn {
  border: 1px solid #cbd5e0;
  background: white;
  color: #4a5568;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.year-nav-btn:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #4299e1;
  color: #4299e1;
  transform: translateY(-1px);
}

.year-nav-btn:active:not(:disabled) {
  transform: translateY(0);
}

.year-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.year-display {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  min-width: 60px;
  text-align: center;
}
</style>
